export type BarLayout = {
  bandEdgesHz: number[];
  bandTiltDb: number[];
  barBands: number[];
};

const VOICE_BAR_LAYOUT: BarLayout = {
  bandEdgesHz: [80, 250, 600, 1200, 2500, 5000],
  bandTiltDb: [0, 0, 10, 14, 18],
  barBands: [3, 1, 0, 2, 4],
};

export const BIRDSONG_BAR_LAYOUT: BarLayout = {
  bandEdgesHz: [2250, 2650, 3100, 3600, 4200, 4950],
  bandTiltDb: [0, 0, 0, 0, 0],
  barBands: [0, 1, 2, 3, 4],
};

const IDLE_BAR_HEIGHT = 4;
const MAX_BAR_HEIGHT = 14;
const LEVEL_RANGE_DB = 40;
const MIN_CEILING_DB = -40;
const CEILING_RELEASE_DB_PER_FRAME = 0.1;
const LEVEL_CURVE = 1.5;
const ATTACK = 0.6;
const RELEASE = 0.2;

export const IDLE_BARS = VOICE_BAR_LAYOUT.barBands.map(() => IDLE_BAR_HEIGHT);

export function createBarMeter(
  sampleRate: number,
  fftSize: number,
  { bandEdgesHz, bandTiltDb, barBands }: BarLayout = VOICE_BAR_LAYOUT
) {
  const binHz = sampleRate / fftSize;
  const bandBins = bandEdgesHz.slice(0, -1).map((lowHz, band) => {
    const start = Math.round(lowHz / binHz);
    const end = Math.max(start + 1, Math.round(bandEdgesHz[band + 1] / binHz));
    return { start, end };
  });
  let ceilingDb = MIN_CEILING_DB;
  let heights = IDLE_BARS;

  return (frequencyDb: Float32Array): number[] => {
    const bandDb = bandBins.map(({ start, end }, band) => {
      let power = 0;
      for (let bin = start; bin < end; bin++) {
        power += 10 ** (frequencyDb[bin] / 10);
      }
      return 10 * Math.log10(power / (end - start)) + bandTiltDb[band];
    });
    ceilingDb = Math.max(
      ...bandDb,
      ceilingDb - CEILING_RELEASE_DB_PER_FRAME,
      MIN_CEILING_DB
    );

    heights = barBands.map((band, bar) => {
      const level = Math.min(
        1,
        Math.max(0, (bandDb[band] - ceilingDb) / LEVEL_RANGE_DB + 1)
      );
      const target =
        IDLE_BAR_HEIGHT +
        level ** LEVEL_CURVE * (MAX_BAR_HEIGHT - IDLE_BAR_HEIGHT);
      const current = heights[bar];
      const easing = target > current ? ATTACK : RELEASE;
      return current + (target - current) * easing;
    });
    return heights;
  };
}
